import React, { useState, useEffect, useMemo } from 'react';
import { Building2, Mail, Phone, Search, Tag, X, ChevronDown, ChevronRight, Check } from 'lucide-react';
import CustomFieldsRenderer, { validateCustomFields } from './CustomFieldsRenderer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_COLORS = {
  A: 'bg-green-100 text-green-800 border-green-200',
  B: 'bg-amber-100 text-amber-800 border-amber-200',
  C: 'bg-blue-100 text-blue-800 border-blue-200',
  D: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  E: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  F: 'bg-orange-100 text-orange-800 border-orange-200',
  G: 'bg-purple-100 text-purple-800 border-purple-200',
  H: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  I: 'bg-rose-100 text-rose-800 border-rose-200',
  J: 'bg-violet-100 text-violet-800 border-violet-200',
  K: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  L: 'bg-teal-100 text-teal-800 border-teal-200',
  M: 'bg-sky-100 text-sky-800 border-sky-200',
  N: 'bg-lime-100 text-lime-800 border-lime-200',
  O: 'bg-stone-100 text-stone-800 border-stone-200',
  P: 'bg-pink-100 text-pink-800 border-pink-200',
  Q: 'bg-red-100 text-red-800 border-red-200',
  R: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
  S: 'bg-slate-100 text-slate-800 border-slate-200',
  T: 'bg-gray-100 text-gray-800 border-gray-200',
  U: 'bg-zinc-100 text-zinc-800 border-zinc-200',
};

const CompanyInfoStep = ({ initialData, onSubmit, loading, color: colorKey = 'emerald', schema = null, profileExtras = {} }) => {
  const P = {
    emerald: { ring: 'focus:ring-emerald-500', btn: 'bg-emerald-600 hover:bg-emerald-700', tabActive: 'bg-emerald-600 text-white', rowSelected: 'bg-emerald-50', checkboxOn: 'bg-emerald-600 border-emerald-600' },
    blue:    { ring: 'focus:ring-blue-500',    btn: 'bg-blue-600 hover:bg-blue-700',       tabActive: 'bg-blue-600 text-white',    rowSelected: 'bg-blue-50',    checkboxOn: 'bg-blue-600 border-blue-600' },
    purple:  { ring: 'focus:ring-purple-500',  btn: 'bg-purple-600 hover:bg-purple-700',   tabActive: 'bg-purple-600 text-white',  rowSelected: 'bg-purple-50',  checkboxOn: 'bg-purple-600 border-purple-600' },
    amber:   { ring: 'focus:ring-amber-500',   btn: 'bg-amber-600 hover:bg-amber-700',     tabActive: 'bg-amber-600 text-white',   rowSelected: 'bg-amber-50',   checkboxOn: 'bg-amber-600 border-amber-600' },
    rose:    { ring: 'focus:ring-rose-500',    btn: 'bg-rose-600 hover:bg-rose-700',       tabActive: 'bg-rose-600 text-white',    rowSelected: 'bg-rose-50',    checkboxOn: 'bg-rose-600 border-rose-600' },
  }[colorKey] || {
    ring: 'focus:ring-emerald-500', btn: 'bg-emerald-600 hover:bg-emerald-700', tabActive: 'bg-emerald-600 text-white', rowSelected: 'bg-emerald-50', checkboxOn: 'bg-emerald-600 border-emerald-600'
  };
  const [formData, setFormData] = useState(initialData || {
    company_name: '', contact_email: '', contact_phone: '', nace_codes: [], primary_nace_code: ''
  });

  // Schema config — lookup helper. Defaults: every builtin = "required".
  const builtinMode = (key) => {
    const cfg = (schema && schema.builtin) || {};
    return (cfg[key] || 'required').toLowerCase();   // 'required' | 'optional' | 'hidden'
  };
  const showBuiltin = (key) => builtinMode(key) !== 'hidden';
  const requiredBuiltin = (key) => builtinMode(key) === 'required';
  const customFields = (schema && schema.custom_fields) || [];

  // Custom fields state — pre-fill from existing extras + saved profile_extras
  const initialExtras = (initialData && initialData.extras) || {};
  const [extrasData, setExtrasData] = useState(() => {
    const result = {};
    for (const f of customFields) {
      if (initialExtras[f.key] !== undefined) result[f.key] = initialExtras[f.key];
      else if (f.type !== 'consent' && profileExtras[f.key] !== undefined) result[f.key] = profileExtras[f.key];
      else if (f.type === 'multi-select' || f.type === 'multiselect') result[f.key] = [];
    }
    return result;
  });
  // Re-hydrate when initialData arrives later (e.g. profile fetch finished after mount).
  useEffect(() => {
    if (initialData && Object.values(initialData).some(v => v && v.length !== 0)) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.company_name, initialData?.contact_email]);
  const [errors, setErrors] = useState({});
  const [naceCodes, setNaceCodes] = useState([]);
  const [search, setSearch] = useState('');
  const [naceLoading, setNaceLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState('all'); // 'all' or section letter

  useEffect(() => {
    fetch(`${API_URL}/api/nace-codes`)
      .then(r => r.json())
      .then(data => { 
        setNaceCodes(data); 
        setNaceLoading(false); 
      })
      .catch(() => setNaceLoading(false));
  }, []);

  // Group codes by section
  const groupedCodes = useMemo(() => {
    const groups = {};
    naceCodes.forEach(n => {
      if (!groups[n.section]) {
        groups[n.section] = { name: n.section_name, codes: [] };
      }
      groups[n.section].codes.push(n);
    });
    return groups;
  }, [naceCodes]);

  // Get section list for tabs
  const sections = useMemo(() => {
    return Object.entries(groupedCodes).map(([code, data]) => ({
      code,
      name: data.name,
      count: data.codes.length
    })).sort((a, b) => a.code.localeCompare(b.code));
  }, [groupedCodes]);

  // Filter codes based on search and active tab
  const filteredCodes = useMemo(() => {
    let codes = naceCodes;
    
    // Filter by section tab
    if (activeTab !== 'all') {
      codes = codes.filter(n => n.section === activeTab);
    }
    
    // Filter by search
    if (search.trim()) {
      const s = search.toLowerCase();
      codes = codes.filter(n => 
        n.code.toLowerCase().includes(s) || 
        n.description.toLowerCase().includes(s) ||
        (n.section_name || '').toLowerCase().includes(s)
      );
    }
    
    return codes.slice(0, 100);
  }, [naceCodes, search, activeTab]);

  const selectedNaces = naceCodes.filter(n => formData.nace_codes.includes(n.code));

  const validate = () => {
    const e = {};
    if (showBuiltin('company_name') && requiredBuiltin('company_name') && !formData.company_name.trim())
      e.company_name = 'Required';
    if (showBuiltin('contact_email')) {
      if (requiredBuiltin('contact_email') && !formData.contact_email.trim())
        e.contact_email = 'Required';
      else if (formData.contact_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email))
        e.contact_email = 'Invalid email';
    }
    if (showBuiltin('contact_phone') && requiredBuiltin('contact_phone') && !(formData.contact_phone || '').trim())
      e.contact_phone = 'Required';
    if (showBuiltin('nace_codes') && requiredBuiltin('nace_codes') && formData.nace_codes.length === 0)
      e.nace_codes = 'Select at least one NACE code';
    // Custom fields
    const { errors: customErrs } = validateCustomFields(customFields, extrasData);
    Object.assign(e, customErrs);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (validate()) onSubmit({ ...formData, extras: extrasData });
  };

  const toggleNace = (code) => {
    const codes = formData.nace_codes.includes(code) 
      ? formData.nace_codes.filter(c => c !== code) 
      : [...formData.nace_codes, code];
    setFormData({ 
      ...formData, 
      nace_codes: codes, 
      primary_nace_code: codes.length === 0 ? '' : (codes.includes(formData.primary_nace_code) ? formData.primary_nace_code : codes[0]) 
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div data-testid="company-info-form" className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
        <p className="text-gray-500 mt-2 text-sm">Enter your company details to begin the assessment</p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Name */}
        {showBuiltin('company_name') && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 text-gray-400" /> Company Name{requiredBuiltin('company_name') ? ' *' : ''}
          </label>
          <input 
            type="text" 
            value={formData.company_name} 
            onChange={e => setFormData({ ...formData, company_name: e.target.value })}
            className={`w-full bg-white border rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 ${P.ring} transition-all ${errors.company_name ? 'border-red-400' : 'border-gray-300'}`}
            placeholder="Enter company name" 
            data-testid="company-name-input" 
          />
          {errors.company_name && <p className="text-red-500 text-xs mt-1">{errors.company_name}</p>}
        </div>
        )}
        
        {/* Contact Info */}
        {(showBuiltin('contact_email') || showBuiltin('contact_phone')) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {showBuiltin('contact_email') && (
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 text-gray-400" /> Contact Email{requiredBuiltin('contact_email') ? ' *' : ''}
            </label>
            <input 
              type="email" 
              value={formData.contact_email} 
              onChange={e => setFormData({ ...formData, contact_email: e.target.value })}
              className={`w-full bg-white border rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 ${P.ring} transition-all ${errors.contact_email ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="company@example.com" 
              data-testid="contact-email-input" 
            />
            {errors.contact_email && <p className="text-red-500 text-xs mt-1">{errors.contact_email}</p>}
          </div>
          )}
          {showBuiltin('contact_phone') && (
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 text-gray-400" /> Phone{requiredBuiltin('contact_phone') ? ' *' : ''}
            </label>
            <input 
              type="tel" 
              value={formData.contact_phone} 
              onChange={e => setFormData({ ...formData, contact_phone: e.target.value })}
              className={`w-full bg-white border ${errors.contact_phone ? 'border-red-400' : 'border-gray-300'} rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 ${P.ring} transition-all`}
              placeholder="+389 XX XXX XXX" 
              data-testid="contact-phone-input" 
            />
            {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
          </div>
          )}
        </div>
        )}
        
        {/* NACE Codes Section */}
        {showBuiltin('nace_codes') && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
            <Tag className="w-4 h-4 text-gray-400" /> NACE Activity Codes * 
            <span className="text-gray-400 font-normal text-xs">({naceCodes.length} available)</span>
          </label>
          
          {/* Selected Codes */}
          {selectedNaces.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <span className="text-xs text-gray-500 w-full mb-1">Selected ({selectedNaces.length}):</span>
              {selectedNaces.map(n => (
                <span 
                  key={n.code} 
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border ${SECTION_COLORS[n.section] || 'bg-gray-100 text-gray-800'}`}
                >
                  <span className="font-bold">{n.code}</span>
                  <span className="max-w-[150px] truncate">{n.description}</span>
                  <button 
                    type="button" 
                    onClick={() => toggleNace(n.code)} 
                    className="hover:opacity-70 ml-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text" 
              value={search} 
              onChange={e => setSearch(e.target.value)}
              className={`w-full bg-white border border-gray-300 rounded-xl pl-10 pr-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 ${P.ring} text-sm transition-all`}
              placeholder="Search by code, description or industry..." 
              data-testid="nace-search" 
            />
            {search && (
              <button 
                type="button" 
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Section Tabs */}
          <div className="flex gap-1 mb-3 overflow-x-auto pb-2 -mx-1 px-1">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                activeTab === 'all' 
                  ? P.tabActive 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {sections.map(s => (
              <button
                key={s.code}
                type="button"
                onClick={() => setActiveTab(s.code)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                  activeTab === s.code 
                    ? P.tabActive 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={s.name}
              >
                {s.code} <span className="text-[10px] opacity-70">({s.count})</span>
              </button>
            ))}
          </div>
          
          {/* NACE Codes List */}
          <div className="max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-xl">
            {naceLoading ? (
              <div className="p-6 text-center text-gray-400 text-sm">Loading NACE codes...</div>
            ) : filteredCodes.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                No matching codes found. Try a different search term.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredCodes.map(n => {
                  const sel = formData.nace_codes.includes(n.code);
                  return (
                    <button 
                      key={n.code} 
                      type="button" 
                      onClick={() => toggleNace(n.code)} 
                      data-testid={`nace-code-${n.code}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                        sel ? P.rowSelected : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        sel ? P.checkboxOn : 'border-gray-300'
                      }`}>
                        {sel && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${SECTION_COLORS[n.section] || 'bg-gray-100'}`}>
                        {n.code}
                      </span>
                      <span className="text-sm text-gray-700 flex-1">{n.description}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded hidden sm:block">
                        {n.section}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          
          {filteredCodes.length >= 100 && (
            <p className="text-xs text-gray-400 mt-2 text-center">
              Showing first 100 results. Use search to find specific codes.
            </p>
          )}
          {errors.nace_codes && <p className="text-red-500 text-xs mt-2">{errors.nace_codes}</p>}
        </div>
        )}
        
        {/* Primary NACE Code */}
        {showBuiltin('nace_codes') && formData.nace_codes.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Primary NACE Code</label>
            <select 
              value={formData.primary_nace_code} 
              onChange={e => setFormData({ ...formData, primary_nace_code: e.target.value })}
              className={`w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-900 outline-none focus:ring-2 ${P.ring} transition-all`} 
              data-testid="primary-nace-select"
            >
              {selectedNaces.map(n => (
                <option key={n.code} value={n.code}>{n.code} - {n.description}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Select the main activity code for your business</p>
          </div>
        )}

        {/* Custom schema-driven fields */}
        {customFields.length > 0 && (
          <div className="border-t border-gray-200 pt-6">
            <CustomFieldsRenderer
              fields={customFields}
              values={extrasData}
              onChange={(k, v) => setExtrasData(prev => ({ ...prev, [k]: v }))}
              errors={errors}
              ringClass={P.ring}
            />
          </div>
        )}
        
        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <button 
            type="submit" 
            disabled={loading} 
            data-testid="begin-questionnaire-btn"
            className={`${P.btn} disabled:opacity-50 text-white px-8 py-3 rounded-xl font-semibold transition-all hover:shadow-lg`}
          >
            {loading ? 'Creating...' : 'Begin Questionnaire'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyInfoStep;
